import { TextField, TextFieldProps } from "@mui/material";
import { Controller, Control, FieldValues, Path } from "react-hook-form";

import styles from "./InputField.module.scss";

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  rules?: object;
  className?: string;
  classNameForLabel?: string;
  classNameForWrapper?: string;
  slotProps?: TextFieldProps["slotProps"];
  helperText?: string;
  type?: React.HTMLInputTypeAttribute | undefined;
  disabled?: boolean;
  errors?: boolean | undefined;
  placeholder?: string;
  inputFilter?: (value: string) => string;
};

export const InputField = <T extends FieldValues>({
  control,
  name,
  label,
  rules,
  className,
  classNameForLabel,
  classNameForWrapper,
  slotProps,
  helperText,
  type,
  placeholder,
  inputFilter,
  disabled = false,
}: Props<T>) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState }) => {
      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        field.onChange(inputFilter ? inputFilter(value) : value);
      };

      return (
        <div
          className={`${styles.wrapper} ${classNameForWrapper ? classNameForWrapper : ""}`}
        >
          <span
            className={`${styles.label} ${
              classNameForLabel ? classNameForLabel : ""
            }`}
          >
            {label}
          </span>

          <TextField
            {...field}
            {...(className && { className })}
            InputProps={{
              sx: {
                color: "white",
              },
            }}
            variant="outlined"
            disabled={disabled}
            fullWidth
            error={!!fieldState.error}
            type={type}
            placeholder={placeholder}
            onChange={handleChange}
            helperText={
              fieldState.error ? fieldState.error.message : helperText || " "
            }
            slotProps={slotProps}
            sx={{
              borderRadius: "40px",
              "& input:-webkit-autofill": {
                WebkitTextFillColor: "rgba(38, 38, 38, 1)",
                caretColor: "rgba(38, 38, 38, 1)",
                transition: "background-color 5000s ease-in-out 0s",
              },

              ".MuiFormHelperText-root": {
                minHeight: "20px",
                marginTop: "4px",
                lineHeight: 1.2,
              },
              ".MuiFormHelperText-root.Mui-error": {
                color: "#FF0002",
              },
              ".MuiFormHelperText-root.Mui-disabled": {
                color: "#FF0002",
                fontWeight: 400,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(164, 187, 199, 0.5)",
                },
                "&.Mui-disabled:hover fieldset": {
                  borderColor: "currentcolor",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(164, 187, 199, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(164, 187, 199, 0.5)",
                },
              },
            }}
          />
        </div>
      );
    }}
  />
);
